import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';

import * as utils from '@chronos/utils';
import { stj } from '@chronos/time-util';

import { useResizable } from '../../Resizable';
import WorklogItem, { isDailyFirstItem, isWeklyLastItem } from '../WorklogItem';
import Button from '../../Button';
import Tooltip from '../../Tooltip';
import PlayIcon from '../../Icons/PlayWhiteIcon';
import UserAvatar from '../../UserAvatar';
import TimeLine from '../TimeLine';
import LabelList from '../../LabelList';
import * as S from './styled';

const WorklogGroupItem = ({ index, style, data }) => {
  const {
    worklogs,
    listItems,
    dayPoints,
    weekPoints,
    cards,
    users,
    isShowLabel,
    isLabelExpanded,
    showStartBtn,
    showTimeline,
    showWorklogButtonsDefault,
    shouldDisplayDivider,
    onClickCardname,
    onClickExpandLabel,
    onClickExpandWorklogGroup,
    onResizeLabelArea,
    onResizeWorglogGroup,
    onStartWorklog,
    reRenderItemsNow,
    isMobile,
    isWorklogGroupExpanded,
    weekStartsOn,
  } = data;

  const worklogGroup = listItems[index];
  const card = cards[worklogGroup.cardId];
  const isExpanded = isWorklogGroupExpanded(worklogGroup.id);

  const ref = useRef();
  const onResize = useCallback(
    (size) => {
      onResizeWorglogGroup(worklogGroup.id, index, size);
    },
    [worklogGroup.id, index]
  );
  useResizable(ref, onResize);

  const [showButtons, setShowButtons] = useState(
    showWorklogButtonsDefault || isMobile
  );

  useEffect(() => {
    setShowButtons(showWorklogButtonsDefault || isMobile);
  }, [showWorklogButtonsDefault, isMobile]);

  const onMouseEnter = () => {
    if (!showWorklogButtonsDefault && !isMobile) {
      setShowButtons(true);
    }
  };
  const onMouseLeave = () => {
    if (!showWorklogButtonsDefault && !isMobile) {
      setShowButtons(false);
    }
  };

  const [groupUsers, usersString] = useMemo(() => {
    const groupUsersObject = worklogGroup.worklogs.reduce((acc, worklogId) => {
      const worklog = worklogs[worklogId];
      const user = users[worklog.accountId];
      if (user) {
        acc[user.id] = {
          ...user,
          avatarSrc: user.avatarUrl ? `${user.avatarUrl}/170.png` : null,
          initials: utils.getMemberInitials(user.fullName),
          seconds: (acc[user.id]?.seconds ?? 0) + worklog.seconds,
        };
      }
      return acc;
    }, {});
    const usersArr = Object.keys(groupUsersObject).map((userId) => ({
      ...groupUsersObject[userId],
      loggedString: stj(groupUsersObject[userId].seconds),
    }));
    return [usersArr, usersArr.map((u) => u.fullName).join(', ')];
  }, [worklogGroup, users]);

  const onClickExpandComment = (commentIndex) => {
    data.onClickExpandComment(`${worklogGroup.id}_${commentIndex}`, false);
    reRenderItemsNow(index);
  };

  const isCommentExpanded = (commentIndex) => {
    return data.isCommentExpanded(`${worklogGroup.id}_${commentIndex}`);
  };

  const onDeleteWorklog = (worklog, worklogIndex) => {
    data.onDeleteWorklog(worklog, worklogIndex, index);
  };

  const shouldShowTimeline =
    showTimeline &&
    isDailyFirstItem({
      index,
      worklogs,
      listItems,
    });

  const showWeekPoint = isWeklyLastItem({
    index,
    worklogs,
    listItems,
    weekStartsOn,
  });

  const worklogsLength = worklogGroup.worklogs.length;

  return (
    <div
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {shouldShowTimeline && (
        <TimeLine
          date={worklogGroup.computed.startedAt}
          secondsByDate={dayPoints?.[worklogGroup.computed.startedAtDayStr]}
          secondsByWeek={
            showWeekPoint
              ? weekPoints?.[worklogGroup.computed.endOfWeekDayStr]
              : null
          }
        />
      )}
      <S.WorklogGroupItem isExpanded={isExpanded}>
        <S.LeftSide>
          <S.AvatarGroup
            size="medium"
            count={groupUsers.length}
          >
            {groupUsers.length === 1 ? (
              <UserAvatar
                src={groupUsers[0].avatarSrc}
                initials={groupUsers[0].initials}
                size="medium"
              />
            ) : (
              groupUsers.map((userData, userIndex) => (
                <S.AvatarGroupItem
                  key={userData.id}
                  index={userIndex}
                >
                  <Tooltip
                    title={`${userData.fullName}: ${userData.loggedString}`}
                  >
                    <UserAvatar
                      src={userData.avatarSrc}
                      initials={userData.initials}
                      size="medium"
                    />
                  </Tooltip>
                </S.AvatarGroupItem>
              ))
            )}
          </S.AvatarGroup>
          <S.CounterBadge
            onClick={() => {
              onClickExpandWorklogGroup(worklogGroup.id, index);
            }}
          >
            x{worklogsLength}
          </S.CounterBadge>
        </S.LeftSide>
        <S.Body>
          <S.Title>
            <S.UserName>{usersString}</S.UserName>
            <S.UserAction>
              <S.ActionText>
                {`logged ${worklogGroup.computed.trackedTime}`}
              </S.ActionText>
            </S.UserAction>
          </S.Title>
          <S.Title>
            <S.CardInfo>
              to
              <S.CardName
                /* textDecoration="underline" */
                onClick={() => {
                  onClickCardname(card);
                }}
              >
                {card?.name || 'Unknown card'}
              </S.CardName>
            </S.CardInfo>
            {showButtons && (
              <S.ManagePanel>
                {showStartBtn && (
                  <Button
                    style={{
                      marginRight: '12px',
                    }}
                    appearance="subtle-link"
                    spacing="none"
                    onClick={() => {
                      if (onStartWorklog) {
                        onStartWorklog(worklogGroup);
                      }
                    }}
                    withIcon
                  >
                    <PlayIcon size="middle" />
                  </Button>
                )}
                <S.ExpandButton
                  onClick={() => {
                    onClickExpandWorklogGroup(worklogGroup.id, index);
                  }}
                >
                  …
                </S.ExpandButton>
              </S.ManagePanel>
            )}
          </S.Title>
          {isShowLabel && card?.labels?.length > 0 && (
            <LabelList
              isExpanded={isLabelExpanded}
              labels={card?.labels}
              onResize={(size) => {
                onResizeLabelArea(worklogGroup.id, index, size);
              }}
              onExpand={() => {
                onClickExpandLabel(index);
              }}
            />
          )}
          <S.Footer>
            <S.FooterInfo>
              <div>{worklogGroup.computed.timePoints}</div>
            </S.FooterInfo>
          </S.Footer>
          {shouldDisplayDivider && !isExpanded && (
            <div style={{ marginLeft: '8px' }}>
              <S.Divider />
            </div>
          )}
        </S.Body>
      </S.WorklogGroupItem>
      <div
        ref={ref}
        style={{
          ...(!isMobile
            ? {
                marginLeft: '40px',
              }
            : {}),
        }}
      >
        {isExpanded && (
          <S.ExpandedWorklogGroupWrapper>
            <S.ExpandedWorklogGroup isMobile={isMobile}>
              {worklogGroup.worklogs.map((worklogId, worklogIndex) => (
                <WorklogItem
                  key={worklogId}
                  index={worklogIndex}
                  data={{
                    ...data,
                    isCommentExpanded,
                    onClickExpandComment,
                    onDeleteWorklog,
                    onResizeCommentArea: null,
                    listItems: worklogGroup.worklogs,
                    shouldDisplayDivider: worklogsLength === worklogIndex + 1,
                    shouldDisplayCard: false,
                    isShowLabel: false,
                    showTimeline: false,
                    showStartBtn: false,
                  }}
                />
              ))}
            </S.ExpandedWorklogGroup>
          </S.ExpandedWorklogGroupWrapper>
        )}
      </div>
    </div>
  );
};

WorklogGroupItem.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  data: PropTypes.shape({
    worklogs: PropTypes.object.isRequired,
    listItems: PropTypes.object.isRequired,
    dayPoints: PropTypes.object,
    weekPoints: PropTypes.object,
    cards: PropTypes.object.isRequired,
    users: PropTypes.object.isRequired,
    isShowLabel: PropTypes.bool.isRequired,
    isLabelExpanded: PropTypes.bool.isRequired,
    showStartBtn: PropTypes.bool.isRequired,
    showTimeline: PropTypes.bool,
    showWorklogButtonsDefault: PropTypes.bool.isRequired,
    shouldDisplayDivider: PropTypes.bool.isRequired,
    onClickCardname: PropTypes.func.isRequired,
    onClickExpandLabel: PropTypes.func,
    onClickExpandWorklogGroup: PropTypes.func,
    onResizeLabelArea: PropTypes.func.isRequired,
    onResizeWorglogGroup: PropTypes.func.isRequired,
    onStartWorklog: PropTypes.func,
    reRenderItemsNow: PropTypes.func,
    isMobile: PropTypes.bool,
    isWorklogGroupExpanded: PropTypes.func,
    onClickExpandComment: PropTypes.func.isRequired,
    isCommentExpanded: PropTypes.func.isRequired,
    weekStartsOn: PropTypes.number,
  }).isRequired,
};

export default WorklogGroupItem;
